import Button from "@/components/bricks/button/Button";
import FlytfirmaIcon from "../../icons/FlytfirmaIcon";
import Modal, { ModalContext } from "../../Modal/Modal";
import { StickyFooter } from "../../stickyFooter/modalStickyFooter";
import { Dialog } from "radix-ui";
import useFirmaMasseFlyt, {
  FirmaMasseFlytDTO,
  MasseFlyttetFirmaDTO,
} from "./useFirmaMasseFlyt";
import { useMutation } from "@tanstack/react-query";
import ValidationErrorMessage from "../../formcontrols/ValidationErrorMessage";
import IsRequired from "../../formcontrols/common/IsRequired";
import { useContext, useLayoutEffect, useRef, useState } from "react";
import Spinner from "../../spinner/Spinner";
import { AlertMessage } from "../../AlertMessage";
import AutoCompleteFirma, {
  AutoCompleteFirmaResponse,
} from "../../autocomplete/AutoCompleteFirma";
import { AutoCompleteFormSelectedItem } from "../../autocomplete/AutoCompleteListItem";
import { AutoCompleteItem } from "../../autocomplete/interface";
import { FirmaDto } from "../../../interfaces/schema";
import { DatePicker } from "rsuite";
import { rsDatePickerLocale } from "../../formcontrols/DatePicker";
import { splitPickerChange } from "../../formcontrols/datePickerValue";
import { dateToyyyyMMdd } from "../../../util/date";
import {
  TicketHeading,
  TicketRoot,
  TicketSub,
  TicketSubItem,
} from "../../ticket/Ticket";
import { WizardReceipt } from "../common/wizardReceipt";

export interface IFirmaMasseFlytProps {
  firma: FirmaDto;
  onSuccess: () => void;
}

type MasseFlytMutationType = ReturnType<
  typeof useMutation<MasseFlyttetFirmaDTO, unknown, FirmaMasseFlytDTO, unknown>
>;
export default function FirmaMasseFlyt(props: IFirmaMasseFlytProps) {
  const { firma, onSuccess } = props;
  const { id } = firma;
  const {
    dryRunMutation,
    mutation,
    step,
    setStep,
    flytteDato,
    nytFirma,
    setFlytteDato,
    setNytFirma,
  } = useFirmaMasseFlyt(id);
  return (
    <Modal
      dialog
      title={
        <>
          <FlytfirmaIcon height={20} /> Masseflytning af medarbejdere
          <span className="c-modal-steps">{step < 3 ? `${step} / 2` : ""}</span>
        </>
      }
      description=""
    >
      {step === 1 && (
        <Step1
          flytteDato={flytteDato}
          setFlytteDato={setFlytteDato}
          nytFirma={nytFirma}
          setNytFirma={setNytFirma}
          nextStep={() => setStep(2)}
          dryRunMutation={dryRunMutation}
          firma={firma}
        />
      )}
      {step === 2 && flytteDato && nytFirma && dryRunMutation.data && (
        <Step2
          flytteDato={dateToyyyyMMdd(flytteDato)}
          nyFirmaId={nytFirma.id}
          fraFirma={firma.navn1 ?? ""}
          nextStep={() => setStep(3)}
          prevStep={() => setStep(1)}
          dryRunMutation={dryRunMutation}
          mutation={mutation}
        />
      )}
      {step === 3 && (
        <Step3
          mutation={mutation}
          fraFirma={firma.navn1 ?? ""}
          onSuccess={onSuccess}
        />
      )}
    </Modal>
  );
}

function Step1(props: {
  nextStep: () => void;
  dryRunMutation: MasseFlytMutationType;
  firma: FirmaDto;
  nytFirma: AutoCompleteItem<AutoCompleteFirmaResponse> | null | undefined;
  setNytFirma: (
    nytFirma: AutoCompleteItem<AutoCompleteFirmaResponse> | null | undefined,
  ) => void;
  flytteDato: Date | null;
  setFlytteDato: (flytteDato: Date | null) => void;
}) {
  const { nytFirma, setNytFirma, flytteDato, setFlytteDato } = props;
  // An unfinished date, kept on screen while flytteDato is null (ADV-320).
  const [draft, setDraft] = useState<Date | null>(null);

  // Turn off autocomplete for input element within RSDatePicker
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (ref.current) {
      const target = (ref.current as any).target as HTMLDivElement;
      const inputs = target.getElementsByTagName("input");
      if (inputs.length > 0) {
        const input = inputs[0] as HTMLInputElement;
        input.autocomplete = "off";
      }
    }
  }, [ref]);

  const [nyFirmaIdTouched, setNyFirmaIdTouched] = useState<boolean>(false);
  const nyFirmaIdError = () => {
    if (!nytFirma) {
      return "Skal være sat";
    }
    return "";
  };

  const [flytteDatoTouched, setFlytteDatoTouched] = useState<boolean>(false);
  const flytteDatoError = () => {
    if (!flytteDato) {
      return "Skal være sat";
    }
    return "";
  };
  return (
    <>
      <div className="c-modal-container">
        <div className="c-item-list c-modal-container-inner">
          <div>
            <label htmlFor="" className="form-label">
              Fra firma
            </label>
            <div className="border rounded w-100 p-3" title="Fra firma">
              {props.firma.navn1}
            </div>
          </div>
          <div className="flex-100">
            <label htmlFor="" className="form-label">
              Til firma
              <IsRequired />
            </label>
            {!nytFirma && (
              <AutoCompleteFirma
                className="w-100"
                isInvalidClass={
                  nyFirmaIdTouched && nyFirmaIdError() ? "is-invalid" : ""
                }
                includeFirstItem={false}
                onSelectedItemChange={(item) => {
                  setNyFirmaIdTouched(true);
                  setNytFirma(item.selectedItem);
                }}
                onInputBlur={() => {
                  setNyFirmaIdTouched(true);
                }}
                focusInputOnMount={true}
              />
            )}
            {nytFirma && (
              <AutoCompleteFormSelectedItem
                title={"Fjern firma"}
                item={nytFirma}
                onClick={() => {
                  setNytFirma(undefined);
                }}
                focusOnMount={true}
              />
            )}
            <ValidationErrorMessage
              touched={nyFirmaIdTouched}
              errorMessage={nyFirmaIdError()}
            />
          </div>

          <div className="flex-100 mt-2">
            <label className="form-label" htmlFor="p-nr">
              Flytdato <IsRequired />
            </label>
            <DatePicker
              isoWeek
              ref={ref}
              aria-autocomplete="none"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  return false;
                }
              }}
              id={"flytteDato"}
              limitEndYear={100}
              oneTap
              className={`flex-100 d-block w-100${
                flytteDatoTouched && flytteDatoError() ? " is-invalid" : ""
              }`}
              placeholder={"dd.mm.åååå"}
              format={"dd.MM.yyyy"}
              disabled={props.dryRunMutation.isPending}
              value={flytteDato ?? draft}
              onBlurCapture={() => {
                setFlytteDatoTouched(true);
              }}
              onChange={(date) => {
                const next = splitPickerChange(date);
                setDraft(next.draft);
                setFlytteDato(next.value);
              }}
              ranges={[
                {
                  label: "I dag",
                  value: new Date(),
                  placement: "bottom",
                },
              ]}
              locale={rsDatePickerLocale}
              placement="autoVerticalStart"
            />
            <ValidationErrorMessage
              touched={flytteDatoTouched}
              errorMessage={flytteDatoError()}
            />
          </div>
        </div>
        {props.dryRunMutation.isError && (
          <div className="c-modal-container-inner mb-2">
            <AlertMessage warningIcon type="danger">
              <p className="mb-0">Der skete en fejl</p>
            </AlertMessage>
          </div>
        )}
      </div>
      <StickyFooter>
        <Dialog.Close asChild>
          <Button color="secondary" type="button" onClick={() => {}}>
            Annuller
          </Button>
        </Dialog.Close>
        <div style={{ position: "relative" }}>
          <Button
            disabled={props.dryRunMutation.isPending}
            color="primary"
            type="button"
            onClick={() => {
              if (
                !nytFirma ||
                !flytteDato ||
                nyFirmaIdError() ||
                flytteDatoError()
              ) {
                setNyFirmaIdTouched(true);
                setFlytteDatoTouched(true);
              } else {
                props.dryRunMutation.mutate({
                  flytteDato: dateToyyyyMMdd(flytteDato),
                  nyFirmaId: nytFirma.id,
                });
              }
            }}
          >
            {props.dryRunMutation.isPending ? <Spinner inline size={14} /> : ""}
            Næste
          </Button>
        </div>
      </StickyFooter>
    </>
  );
}
function Step2(props: {
  flytteDato: string;
  nyFirmaId: string;
  fraFirma: string;
  nextStep: () => void;
  prevStep: () => void;
  dryRunMutation: MasseFlytMutationType;
  mutation: MasseFlytMutationType;
}) {
  const { dryRunMutation, fraFirma } = props;
  const { data: dryRunResult } = dryRunMutation;
  return (
    <>
      <div className="c-modal-container">
        <div className="c-item-list c-modal-container-inner">
          {!dryRunResult ? (
            <AlertMessage type="danger">
              <p className="mb-0">Der skete en fejl</p>
            </AlertMessage>
          ) : dryRunResult.fejl.length === 0 &&
            dryRunResult.personRelationer.length > 0 ? (
            <AlertMessage
              notificationIcon
              heading={`Klar til at flytte ${
                dryRunResult.personRelationer.length
              } medarbejder${
                dryRunResult.personRelationer.length !== 1 ? "e" : ""
              } fra ${fraFirma} til ${dryRunResult.firma.navn1}`}
              type="success"
            >
              <p>Klik "Udfør" for at gemme ændringer.</p>
            </AlertMessage>
          ) : (
            <AlertMessage
              warningIcon
              type="warning"
              heading={
                dryRunResult.personRelationer.length === 0
                  ? "Ingen medarbejdere kan flyttes fra " +
                    fraFirma +
                    " til " +
                    dryRunResult.firma.navn1
                  : `Klar til at flytte ${dryRunResult.personRelationer.length}
                medarbejder${
                  dryRunResult.personRelationer.length !== 1 ? "e" : ""
                } fra ${fraFirma} til ${dryRunResult.firma.navn1}`
              }
            >
              <>
                {dryRunResult.fejl.length > 0 && (
                  <p>
                    {dryRunResult.fejl.length} medarbejder
                    {dryRunResult.fejl.length !== 1 ? "e" : ""} kan ikke
                    flyttes:
                  </p>
                )}
                <>
                  {dryRunResult.fejl.map((fejl, index) => {
                    return (
                      <TicketRoot
                        title="Klik for at åbne person i nyt vindue"
                        icon="arrow"
                        className="bg-white shadow-sm mb-4"
                        key={index}
                        onClick={() =>
                          window.open(
                            `/person/${fejl.person.id}`,
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                      >
                        <TicketHeading>
                          <h4>
                            {fejl.person.fornavne} {fejl.person.efternavn}
                          </h4>
                        </TicketHeading>
                        <TicketSub>
                          <TicketSubItem>
                            Fejlbesked: {fejl.fejlBesked}
                          </TicketSubItem>
                        </TicketSub>
                      </TicketRoot>
                    );
                  })}
                  {dryRunResult.personRelationer.length > 0 && (
                    <p>Klik "Udfør" for at gemme ændringer.</p>
                  )}
                  {dryRunResult.personRelationer.length === 0 && (
                    <p>
                      Klik "Tilbage" for at vælge et andet firma. Klik "Luk" for
                      at annullere.
                    </p>
                  )}
                </>
              </>
            </AlertMessage>
          )}
        </div>
      </div>
      <StickyFooter>
        <Button
          disabled={props.mutation.isPending}
          color="secondary"
          type="button"
          onClick={() => {
            props.prevStep();
          }}
        >
          Tilbage
        </Button>
        {dryRunResult?.personRelationer.length === 0 && (
          <Dialog.Close asChild>
            <Button color="primary" type="button" onClick={() => {}}>
              Luk
            </Button>
          </Dialog.Close>
        )}
        {!(dryRunResult?.personRelationer.length === 0) && (
          <Button
            disabled={props.mutation.isPending}
            color="primary"
            type="button"
            onClick={() => {
              props.mutation.mutate({
                flytteDato: props.flytteDato,
                nyFirmaId: props.nyFirmaId,
              });
            }}
          >
            Udfør
          </Button>
        )}
      </StickyFooter>
    </>
  );
}
function Step3(props: {
  mutation: MasseFlytMutationType;
  fraFirma: string;
  onSuccess: () => void;
}) {
  const {
    mutation: { data: mutationResult },
    onSuccess,
    fraFirma,
  } = props;

  const { setModalOpen } = useContext(ModalContext);
  return (
    <>
      <div className="c-modal-container">
        <WizardReceipt
          heading={mutationResult ? `Færdig` : "Noget gik galt"}
          icon={mutationResult ? "success" : "warning"}
        >
          <AlertMessage
            warningIcon={!mutationResult}
            notificationIcon={!!mutationResult}
            type={
              !mutationResult
                ? "danger"
                : mutationResult.fejl.length > 0
                  ? "warning"
                  : "success"
            }
            heading={
              !mutationResult
                ? "Kunne ikke gemme ændringer"
                : `${mutationResult.personRelationer.length} medarbejder${
                    mutationResult.personRelationer.length !== 1 ? "e" : ""
                  } er
            flyttet fra ${fraFirma} til ${mutationResult.firma.navn1}`
            }
          >
            <>
              {!mutationResult ? (
                <></>
              ) : mutationResult.fejl.length === 0 ? (
                <></>
              ) : (
                <>
                  <p>
                    {mutationResult.fejl.length} medarbejder
                    {mutationResult.fejl.length !== 1 ? "e" : ""} kunne ikke
                    flyttes:
                  </p>
                  {mutationResult.fejl.map((fejl, index) => {
                    return (
                      <TicketRoot
                        title="Klik for at åbne person i nyt vindue"
                        icon="arrow"
                        className="bg-white shadow-sm mb-4"
                        key={index}
                        onClick={() =>
                          window.open(
                            `/person/${fejl.person.id}`,
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                      >
                        <TicketHeading>
                          <h4>
                            {fejl.person.fornavne} {fejl.person.efternavn}
                          </h4>
                        </TicketHeading>
                        <TicketSub>
                          <TicketSubItem>
                            Fejlbesked: {fejl.fejlBesked}
                          </TicketSubItem>
                        </TicketSub>
                      </TicketRoot>
                    );
                  })}
                </>
              )}
            </>
          </AlertMessage>
        </WizardReceipt>
      </div>
      <StickyFooter>
        <div>&nbsp;</div>
        <Button
          color="primary"
          type="button"
          onClick={() => {
            setModalOpen(false);
            onSuccess();
          }}
        >
          Luk
        </Button>
      </StickyFooter>
    </>
  );
}
